
import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DataStore } from 'aws-amplify/datastore';
import { Patient } from '../../models';

// Define route access configurations
const routeAccess = {
  '/dashboard': ['user'],
  '/create-patient': ['user'],
  '/messages': ['user', 'HCPs', 'Admin'],
  '/doctor/messages': ['HCPs', 'Admin'],
  '/history': ['user'],
  '/profile': ['user', 'HCPs'],
  '/docdashboard': ['HCPs'],
  '/admin': ['Admin'],
  '/prescription-renewal': ['user'],
  '/prescription-managment': ['user', 'HCPs', 'Admin'],
  '/verification-complete': ['user', 'HCPs', 'Admin'],
  '/records/patient': ['HCPs', 'Admin']
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuthenticator();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [hasPatient, setHasPatient] = useState(false);
  const [isPatientCheckComplete, setIsPatientCheckComplete] = useState(false);

  const checkUserStatus = useCallback(async () => {
    if (!user) {
      setIsChecking(false);
      return;
    }

    try {
      const { tokens } = await fetchAuthSession();
      const groups = tokens?.accessToken?.payload?.["cognito:groups"] || [];
      
      let role = 'user';
      if (groups.includes('Admin')) {
        role = 'Admin';
      } else if (groups.includes('HCPs')) {
        role = 'HCPs';
      }
      setUserRole(role);

      if (role === 'user') {
        const patients = await DataStore.query(Patient, p => p.userPatientsId.eq(user.userId));
        setHasPatient(patients.length > 0);
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      setUserRole('user'); // Default to 'user' on error
    } finally {
      setIsChecking(false);
      setIsPatientCheckComplete(true);
    }
  }, [user]);
  
  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  if (isChecking || (userRole === 'user' && !isPatientCheckComplete)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (userRole === 'user' && !hasPatient && location.pathname !== '/create-patient' && !location.state?.patientJustCreated) {
    return <Navigate to="/create-patient" replace />;
  }
  
  // Find the appropriate access configuration by matching path prefix
  const matchingRoute = Object.keys(routeAccess).find(route => 
    location.pathname.startsWith(route)
  );
  
  // Get allowed roles for the matching route
  const allowedRoles = matchingRoute ? routeAccess[matchingRoute] : [];
  
  let hasAccess = false;
  if (userRole === 'Admin') { // Admin has access to all protected routes by default in this setup
    hasAccess = true;
  } else if (userRole === 'HCPs') {
    // HCPs can access their own routes and routes also allowed for 'user' if applicable
    hasAccess = allowedRoles.includes('HCPs') || allowedRoles.includes('user');
  } else { // 'user' role
    hasAccess = allowedRoles.includes('user');
  }

  // Specific check for /records/patient/:patientId to ensure only HCPs/Admins access it.
  // This overrides the general logic if a 'user' somehow was determined to have access.
  if (location.pathname.startsWith('/records/patient') && userRole === 'user') {
    hasAccess = false;
  }
  
  if (!hasAccess) {
    const dashboardRoutes = {
      'Admin': '/admin',
      'HCPs': '/docdashboard',
      'user': '/dashboard'
    };
    // Fallback to user dashboard if role somehow doesn't match keys
    const redirectPath = dashboardRoutes[userRole] || '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }
  
  return children;
};

export default ProtectedRoute;